"use client";

import { useId } from "react";
import { parsePositiveQuantity } from "@shared/validation/positive-number";
import { CalendarDays } from "@/components/icons";
import { format, parseISO, startOfDay } from "date-fns";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { PopoverCalendar } from "@/components/ui/popover-calendar";
import { cn } from "@/lib/utils";
import { getEarliestDeliveryDate } from "@shared/formats/calendar-date";

export function QuoteQuantityField({
  displayValue,
  onChange,
  onBlur,
  error,
}: {
  displayValue: string;
  onChange: (raw: string) => void;
  onBlur?: () => void;
  error?: string;
}) {
  const errorId = useId();
  const invalid = Boolean(error) || parsePositiveQuantity(displayValue) === null;
  return (
    <div className="space-y-1">
      <label
        htmlFor={`${errorId}-quantity`}
        className="block text-xs font-medium text-foreground"
      >
        Quantity
      </label>
      <Input
        id={`${errorId}-quantity`}
        type="text"
        inputMode="numeric"
        value={displayValue}
        className="text-center"
        aria-invalid={invalid}
        aria-describedby={invalid ? errorId : undefined}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur} variant="mono"/>
      {invalid && (
        <span
          id={errorId}
          role="alert"
          className="block text-xs text-destructive-strong"
        >
          {error ?? "Enter a positive whole number."}
        </span>
      )}
    </div>
  );
}

interface DatePickerProps {
  error?: string;
  value: string;
  onChange: (date: string) => void;
  leadTimeDays: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QuoteDeliveryDateField({
  fulfillmentType,
  ...picker
}: DatePickerProps & { fulfillmentType: string }) {
  const errorId = useId();
  return (
    <div className="space-y-1">
      <label className="block space-y-1">
        <span className="text-xs font-medium text-foreground">
          Preferred {fulfillmentType} date
        </span>
        <PreferredDatePicker
          {...picker}
          errorId={picker.error ? errorId : undefined}
        />
      </label>
      {picker.error ? (
        <p id={errorId} className="text-xs text-destructive-strong">
          {picker.error}
        </p>
      ) : null}
    </div>
  );
}

function PreferredDatePicker({
  value,
  onChange,
  leadTimeDays,
  open,
  onOpenChange,
  errorId,
}: DatePickerProps & { errorId?: string }) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          className={cn(
            "w-full justify-between",
            !value && "text-muted-foreground",
          )}
        >
          {value ? format(parseISO(value), "PPP") : "Choose a date"}
          <CalendarDays className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <PopoverCalendar
          mode="single"
          selected={value ? parseISO(value) : undefined}
          onSelect={(selectedDate) => {
            onChange(
              selectedDate
                ? format(startOfDay(selectedDate), "yyyy-MM-dd")
                : "",
            );
            onOpenChange(false);
          }}
          disabled={(date) =>
            date < parseISO(getEarliestDeliveryDate(leadTimeDays))
          }
        />
      </PopoverContent>
    </Popover>
  );
}
